<template>
  <BlocksCard class="blocks-gallery">
    <div ref="galleryRef">
      <!-- Gallery heading -->
      <h2 v-if="heading" class="__heading">
        {{ heading }}
      </h2>

      <!-- Gallery items slider -->
      <BlocksSlider v-model="selectedSlide">
        <div v-for="(image, index) of images" :key="`gallery-image-${index}`" class="floor-plan-item" :class="{'--fullscreen-view': clickToViewFullScreen !== false}" @click="triggerFullScreen()">
          <!-- Gallery item image -->
          <div v-if="typeof image?.url === 'string'" class="__image-wrapper">
            <nuxt-img :src="image.url" :alt="altText" width="100%" height="auto" format="webp" />
          </div>

          <!-- Gallery item caption -->
          <span v-if="image.caption" class="__image-caption">{{ image.caption }}</span>
        </div>
      </BlocksSlider>

      <!-- Image Fullscreen Popup -->
      <BlocksGalleryFullScreen v-if="images.length > 0" v-model="isFullScreenOpen" :selected-image-index="selectedSlide" :images="images" :analytics-event-data="analyticsEventData" />
    </div>
  </BlocksCard>
</template>

<script lang="ts" setup>
import {PropType, Ref} from 'vue';
import {ImageItem} from '~/@types/common';
import {AnalyticsDataProps} from '~/@types/analytics';

// PROPS Definitions
//----------------------------------------------------------------------------------------------------------------------
const props = defineProps({
  heading: {
    type: String,
    default: '',
  },
  images: {
    type: Array as PropType<ImageItem[]>,
    default: () => [],
  },
  altText: {
    type: String,
    required: true,
  },
  clickToViewFullScreen: {
    type: Boolean,
    default: false,
  },
  analyticsEventData: {
    type: [Object, null] as PropType<AnalyticsDataProps>,
    default: () => null,
  },
});

// DATA Definitions
//----------------------------------------------------------------------------------------------------------------------
const isFullScreenOpen: Ref<boolean> = ref(false);
const selectedSlide: Ref<number> = ref(0);
const galleryRef: Ref<HTMLElement | null> = ref(null);
let observer: Ref<IntersectionObserver | null> = ref(null);
const isSectionVisible: Ref<boolean> = ref(false);

/**
 * @_MOUNTED Hook
 */
onMounted(() => {
  initViewTracker();
});

/**
 * @_WATCH images
 */
watch(() => props.images, (newVal, oldVal) => {
  if (oldVal && newVal && newVal !== oldVal) {
    initViewTracker(true);
  }
});

/**
 * Track when the section is visible in the viewport
 *
 * @param forceRefresh
 */
function initViewTracker (forceRefresh = false) {
  if (forceRefresh) {
    // Stop observing the element when the component is unmounted
    if (galleryRef.value && observer.value) {
      observer.value.unobserve(galleryRef.value);
    }
  }

  // Only proceed if galleryRef exists and Observer is supported
  if (galleryRef.value && 'IntersectionObserver' in window) {
    observer.value = new IntersectionObserver((entries) => {
      // entries[0] is this component
      if (entries[0].isIntersecting && !isSectionVisible.value) {
        isSectionVisible.value = true;
      }
    }, {
      threshold: 0.8, // adjust this value to control when the observer callback should be fired
    });

    // Start observing the element
    observer.value.observe(galleryRef.value);
  }
}

/**
 * Trigger full screen view
 */
function triggerFullScreen() {
  if (props.clickToViewFullScreen !== false) {
    isFullScreenOpen.value = true;
  }
}
</script>

<style lang="scss" scoped>
.blocks-gallery {
  .floor-plan-item {
    &.--fullscreen-view {
      cursor: pointer;

      &:hover {
        transform: scale(1.01);
      }
    }
  }
}
</style>
